import { useMemo } from 'react';

import path from 'path';

import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import ErrorPage from '~components/errorBoundary/errorPage';
import ErrorType from '~components/errorBoundary/errorType';
import { pagePathSegments, queryParameters } from '~constants';
import OrganisationsService from '~features/organisations/services/organisationsService';
import PageLayout from '~layouts/page/pageLayout';
import { getService } from '~services/serviceFactory';

import KeyGroupDetailsContainer from '../containers/keyGroupDetailsContainer';
import KeyGroupProvider from '../providers/keyGroupProvider';
import KeyGroupService from '../services/keyGroupService';

const keyGroupService = getService(KeyGroupService);
const organisationsService = getService(OrganisationsService);

const KeyGroupDetailsPage = () => {
	const { t } = useTranslation();
	const { id: keyGroupId } = useParams();
	const [searchParams] = useSearchParams();
	const overwrittenClientId = searchParams.get(queryParameters.OverwrittenClientId) ?? undefined;

	const {
		data: keyGroup,
		isLoading,
		error,
	} = useSWR(
		keyGroupService.getKeyGroupByIdCacheKey(keyGroupId, overwrittenClientId),
		([_, keyGroupId, overwrittenClientId]) =>
			keyGroupService.getKeyGroupById({
				keyGroupId: keyGroupId!,
				overwrittenClientId,
			})
	);

	const { data: organisation, isLoading: isLoadingOrganisation } = useSWR(
		organisationsService.getOrganisationByIdCacheKey(overwrittenClientId),
		([_, id]) => (id ? organisationsService.getOrganisationById(id) : null)
	);

	const crumbs = useMemo(
		() => [
			...(organisation ?
				[
					{
						title: organisation?.name ?? overwrittenClientId,
						path: path.join('/', pagePathSegments.Organisations, overwrittenClientId ?? ''),
					},
				]
			:	[]),
			{
				title: t('keyGroup.labels.plural', { ns: 'entities' }),
				path: path.join('/', pagePathSegments.KeyGroups),
			},
			{
				title: keyGroup?.name ?? '',
				path: '',
			},
		],
		[organisation, keyGroup]
	);

	// --- All hooks above, all early returns below ---

	if (keyGroupId == undefined) {
		return <Navigate to={pagePathSegments.Keys} replace />;
	}

	if (!isLoading && error) {
		return <ErrorPage type={ErrorType.NotFound} />;
	}

	return (
		<KeyGroupProvider
			value={{
				keyGroup: keyGroup,
				isLoading: isLoading || isLoadingOrganisation,
				hasError: error != undefined,
				overwrittenClientId: overwrittenClientId,
			}}
		>
			<PageLayout
				pageTitle={keyGroup?.name ?? ''}
				headerInfo={{
					title: keyGroup?.name,
				}}
				crumbs={crumbs}
			>
				<KeyGroupDetailsContainer />
			</PageLayout>
		</KeyGroupProvider>
	);
};

export default KeyGroupDetailsPage;
